import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'utils/hooks';

import sanitize from 'utils/sanitize';
import { renderModal } from 'utils/modal_util';

import Button from 'components/shared/Button';
import Alert from 'components/shared/Alert';
import Modal from 'components/shared/Modal';
import SubcatalogButton from 'components/shared/SubcatalogButton';

import canvases from 'domains/canvases';
import activeEditor from 'domains/active_editor';

import type { SubcatalogAPIData } from 'types/catalog_types';

type SubcatalogButtonContainerProps = {
  subcatalog: SubcatalogAPIData;
  handleOpenTooltipModal: (id: number, triggerId: string) => void;
  setLeaveFlyoutOpen: (set: boolean) => void;
  setFlyoutOpen: (set: boolean) => void;
};

function SubcatalogButtonContainer({
  subcatalog,
  handleOpenTooltipModal = () => {},
  setLeaveFlyoutOpen,
  setFlyoutOpen,
}: SubcatalogButtonContainerProps) {
  const { name, image_url: imageUrl, id } = subcatalog;

  const rememberPreferenceChecked = useRef(false);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showAddOrChangeDialog, setShowAddOrChangeDialog] = useState(false);

  const dispatch = useAppDispatch();
  const canvasIds = useAppSelector(canvases.selectors.selectIds);
  const activeCanvasId = useAppSelector(canvases.selectors.activeId);
  const subcatalogs = useAppSelector(activeEditor.selectors.subcatalogs);
  const activeProduct = useAppSelector(activeEditor.selectors.product);
  const activeSubcatalogId = useAppSelector(activeEditor.selectors.subcatalogId);

  const isSelected = id === activeSubcatalogId;

  const preferenceLocalStorageKey = 'subcatalogCustomArrangementPreference';
  const [arrangementPreference, setArrangementPreference] = useState<'new' | 'replace' | null>(
    null
  );

  useEffect(() => {
    const userSetPreference = localStorage.getItem(
      preferenceLocalStorageKey
    ) as typeof arrangementPreference;
    setArrangementPreference(userSetPreference);
  }, []);

  const supportedArrangementSubcatalogs = useMemo(() => {
    return subcatalogs.filter((s) => s.arrangement);
  }, [subcatalogs]);

  const subcatalogNameListItems = useMemo(() => {
    return supportedArrangementSubcatalogs.map((s) => <li key={s.id}>{s.name}</li>);
  }, [supportedArrangementSubcatalogs]);

  const hasCustomArrangement = useMemo(() => {
    return supportedArrangementSubcatalogs.find((s) => s.id === id);
  }, [supportedArrangementSubcatalogs, id]);

  const hasCustomArrangementNotice = useMemo(() => {
    return !hasCustomArrangement && canvasIds.length > 1;
  }, [hasCustomArrangement, canvasIds]);

  function handleRememberSelection(e: React.ChangeEvent<HTMLInputElement>) {
    rememberPreferenceChecked.current = e.target.checked;
  }

  const alertMessageHTML = (
    <React.Fragment>
      <span>Changing to {name} will remove your custom arrangement.</span>
      <span>Products that can be added to your arrangement include:</span>
      <ul>{subcatalogNameListItems}</ul>
      <span>Do you want to continue?</span>
    </React.Fragment>
  );

  const addOrChangeButtons = (
    <React.Fragment>
      <Button
        onClick={() => {
          handleModalButtonSelect('new');
        }}
      >
        Add New
      </Button>
      <Button
        onClick={() => {
          handleModalButtonSelect('replace');
        }}
      >
        Replace Current
      </Button>
      <Button
        type="cancel"
        onClick={() => {
          handleModalButtonSelect('cancel');
        }}
      >
        Cancel
      </Button>
    </React.Fragment>
  );

  function handleSubcatalogClick() {
    if (canvasIds.length === 1) {
      // one canvas, just change subcatalog
      handleChangeSubcatalog(false);
    } else if (!hasCustomArrangement) {
      // subcatalog doesn't support custom arrangement
      setShowConfirmDialog(true);
    } else if (!arrangementPreference) {
      // user did not check 'remember preference'
      setShowAddOrChangeDialog(true);
      setLeaveFlyoutOpen(true);
    } else if (arrangementPreference === 'new') {
      // user preference to add new product
      handleChangeSubcatalog(true);
    } else if (arrangementPreference === 'replace') {
      // user preference to replace current product
      handleChangeSubcatalog(false);
    }
  }

  function handleChangeSubcatalog(shouldAddNew: boolean) {
    if (shouldAddNew) {
      dispatch(canvases.actions.createCanvas({ duplicate: true }));
    }

    setShowAddOrChangeDialog(false);
    setFlyoutOpen(false);
    dispatch(canvases.actions.changeActiveCanvasConfig.subcatalog(id));
  }

  function handleModalButtonSelect(button: 'new' | 'replace' | 'cancel') {
    if (rememberPreferenceChecked.current) {
      localStorage.setItem(preferenceLocalStorageKey, button);
    }

    if (button === 'cancel') {
      setShowAddOrChangeDialog(false);
    } else {
      handleChangeSubcatalog(button === 'new');
    }

    setLeaveFlyoutOpen(false);
    setFlyoutOpen(false);
  }

  function openTooltipModal(clickedButtonId: string) {
    setLeaveFlyoutOpen(true);
    handleOpenTooltipModal(id, clickedButtonId);
  }

  return (
    <React.Fragment>
      <SubcatalogButton
        title={sanitize(name)}
        id={id}
        imageUrl={imageUrl}
        isSelected={isSelected}
        hasCustomArrangementNotice={hasCustomArrangementNotice}
        hasTooltip={!!subcatalog.tooltip_title}
        openTooltipModal={openTooltipModal}
        onClick={handleSubcatalogClick}
      />

      {showAddOrChangeDialog &&
        renderModal(
          <Modal
            labelledBy="add-new-change-product-modal"
            title="Add New Product or Replace Current"
            lastFocusedElementId="sidebar-header"
            onClose={() => setShowAddOrChangeDialog(false)}
            primaryFooterGroup={addOrChangeButtons}
          >
            <div className="arrangement-subcatalog-modal">
              <p>
                Do you want to add a new <strong>{name}</strong> product or replace your currently
                selected product <strong>({activeProduct?.name_long})</strong>?
              </p>

              <div>
                <input
                  id="remember-change-selection"
                  type="checkbox"
                  onChange={handleRememberSelection}
                />
                <label htmlFor="remember-change-selection">Remember this selection</label>
              </div>
            </div>
          </Modal>
        )}

      {showConfirmDialog &&
        renderModal(
          <Alert
            title="Confirm Selection"
            labelledBy="confirm-change-subcatalog-alert"
            lastFocusedElementId="sidebar-header"
            messageHTML={alertMessageHTML}
            onContinue={() => {
              setShowConfirmDialog(false);
              setFlyoutOpen(false);
              dispatch(canvases.actions.resetToSingleCanvas(activeCanvasId));
              dispatch(canvases.actions.changeActiveCanvasConfig.subcatalog(id));
            }}
            onClose={() => setShowConfirmDialog(false)}
            onCancel={() => setShowConfirmDialog(false)}
          />
        )}
    </React.Fragment>
  );
}

export default SubcatalogButtonContainer;
